import React, { createContext, useContext, useEffect, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '../utils/supabase';

// ------------------------------------
// INTERFACES AND CONTEXT SETUP
// ------------------------------------

interface AuthContextType {
  user: User | null;
  loading: boolean;
  token: string | null;
  signIn: (emailOrPhone: string, password: string) => Promise<void>;
  signUp: (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    phoneNumber: string
  ) => Promise<void>;
  signOut: () => Promise<void>;
}

// Example: src/contexts/AuthContext.tsx

interface AuthContextType {
  user: User | null; // Assuming 'User' is your auth object type
  loading: boolean;
  // ... other methods like signIn, signOut ...

  // VVV ADD THIS LINE VVV
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// ------------------------------------
// AUTH PROVIDER COMPONENT
// ------------------------------------

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    // Initial session check
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setToken(session?.access_token ?? null);
      setLoading(false);
    });

    // Listener for auth state changes
    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      setToken(session?.access_token ?? null);
    });

    return () => {
      listener?.subscription?.unsubscribe();
    };
  }, []);
  const emailRedirectTo = import.meta.env.VITE_APP_EMAIL_REDIRECT_TO;


  // Sign up: Submits email as primary login, saves other data as metadata
  const signUp = async (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    phoneNumber: string
  ) => {
    // Phone number and names are passed in the 'data' option and saved to raw_user_meta_data
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          first_name: firstName,
          last_name: lastName,
          full_name: `${firstName} ${lastName}`,
          phone_number: phoneNumber, // Saved as metadata
          terms_accepted: true,
          terms_accepted_at: new Date().toISOString()
        },
        emailRedirectTo: emailRedirectTo,
      },
    });
    if (error) throw error;
  };

  // Sign in: Supports email **or** phone (Indian), plus password
  const signIn = async (emailOrPhone: string, password: string) => {
    // Phone detection logic for Indian numbers
    const isPhone = /^(\+91)?[6-9][0-9]{9}$/.test(emailOrPhone.replace(/\s|-/g, ''));
    let params: any = { password };
    if (isPhone) {
      // Supabase recommends standardizing phone format, e.g., '+91XXXXXXXXXX'
      params.phone = emailOrPhone.startsWith('+91')
        ? emailOrPhone
        : '+91' + emailOrPhone.replace(/\s|-/g, '');
    } else {
      params.email = emailOrPhone;
    }
    const { error } = await supabase.auth.signInWithPassword(params);
    if (error) throw error;
  };

  // Sign out
  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      // Clear user state
      setUser(null);
      setToken(null);

      // Clear all localStorage data
      try {
        localStorage.removeItem('onboarding_complete');
        // Clear any other app-specific data
        localStorage.removeItem('user_context');
        localStorage.removeItem('active_gym');
      } catch (e) {
        console.warn('Failed to clear localStorage:', e);
      }
    } catch (error) {
      console.error('Error during sign out:', error);
      // Even if signOut fails, clear local state
      setUser(null);
      setToken(null);
      throw error;
    }
  };

  const refreshUser = async () => {
    const { data, error } = await supabase.auth.refreshSession();
    if (error) throw error;
    setUser(data.session?.user ?? null);
    setToken(data.session?.access_token ?? null);
  };

  const value: AuthContextType = {
    user,
    loading,
    token,
    signIn,
    signUp,
    signOut,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}